import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableChartIcon from '@mui/icons-material/TableChart';
import SyncIcon from '@mui/icons-material/Sync';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, writeBatch, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { alpha } from '@mui/material/styles';

const CategoryCSVImport = ({ categories, open, onClose }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '', isProcessing: false });
  
  // CSV mapping state
  const [csvData, setCsvData] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columnMappings, setColumnMappings] = useState({});
  const [autoMatchedColumns, setAutoMatchedColumns] = useState(new Set()); // Track which columns were auto-matched
  const [propertyNumberColumn, setPropertyNumberColumn] = useState('');
  const [valueFilters, setValueFilters] = useState({}); // Track which values to include for each column
  const [duplicates, setDuplicates] = useState({}); // Track duplicate property numbers
  const [duplicateResolutions, setDuplicateResolutions] = useState({}); // Track user's choice for duplicates
  const [duplicatesReviewed, setDuplicatesReviewed] = useState(false); // Track if duplicates have been reviewed
  const [step, setStep] = useState('upload'); // 'upload', 'mapping', 'processing', 'duplicates'



  // Auto-match CSV column headers to existing category fields
  const autoMatchColumn = (csvHeader) => {
    if (!csvHeader) return '';
    
    const headerLower = csvHeader.toLowerCase().trim();
    
    // Define mapping patterns for common column names
    const mappingPatterns = {
      // PMS systems
      'pms': ['pms'],
      
      // ERP systems  
      'erp': ['erp'],
      
      // Kiosk/Self service stations
      'mewsKiosk': [
        'number of self service station',
        'self service station',
        'kiosk',
        'mews kiosk',
        'self service',
        'selfservice',
        'kiosks'
      ],
      
      // Add more mappings as needed
      // Format: 'categoryFieldName': ['csv_pattern1', 'csv_pattern2', ...]
    };
    
    // Find all available category fields
    const availableFields = [];
    categories.forEach(group => {
      group.items.forEach(item => {
        if (!item.isArchived) {
          availableFields.push({
            fieldName: item.fieldName,
            title: item.title.toLowerCase(),
            titleOriginal: item.title
          });
        }
      });
    });
    
    // Try exact matches first (case-insensitive)
    for (const field of availableFields) {
      if (field.title === headerLower || field.fieldName.toLowerCase() === headerLower) {
        return field.fieldName;
      }
    }
    
    // Try pattern matching
    for (const [fieldName, patterns] of Object.entries(mappingPatterns)) {
      // Find the actual category field that matches this pattern key
      const matchingField = availableFields.find(field => 
        field.fieldName.toLowerCase() === fieldName.toLowerCase() ||
        field.title.includes(fieldName.toLowerCase())
      );
      
      if (matchingField) {
        // Check if CSV header matches any of the patterns
        for (const pattern of patterns) {
          if (headerLower.includes(pattern) || pattern.includes(headerLower)) {
            return matchingField.fieldName;
          }
        }
      }
    }
    
    // Try fuzzy matching for common variations
    for (const field of availableFields) {
      const fieldTitle = field.title;
      
      // Remove common words and check similarity
      const cleanHeader = headerLower
        .replace(/number of/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      const cleanField = fieldTitle
        .replace(/number of/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      
      if (cleanHeader === cleanField) {
        return field.fieldName;
      }
      
      // Check if one contains the other (for partial matches)
      if (cleanHeader.length > 3 && cleanField.includes(cleanHeader)) {
        return field.fieldName;
      }
      if (cleanField.length > 3 && cleanHeader.includes(cleanField)) {
        return field.fieldName;
      }
    }
    
    return ''; // No match found - ignore this column
  };

  // Helper function to extract property number from location string
  const extractPropertyNumber = useCallback((locationString) => {
    if (!locationString) return '';
    const str = String(locationString).trim();
    
    // Look for pattern like "DK032 - Hotel Name" and extract "DK032"
    const match = str.match(/^([A-Z]{2}\d{3})\s*-/);
    if (match) {
      return match[1];
    }
    
    // If no pattern match, check if it's already just a property number
    if (/^[A-Z]{2}\d{3}$/.test(str)) {
      return str;
    }
    
    return str; // Return as-is if no pattern found
  }, []); // No dependencies

  // Helper function to validate property number
  const validatePropertyNumber = (propertyNumber) => {
    if (!propertyNumber) return false;
    // Remove any whitespace
    propertyNumber = propertyNumber.toString().trim();
    // Check if it's empty after trimming
    if (propertyNumber === '') return false;
    return true;
  };

  // Helper function to validate category value based on type
  const validateCategoryValue = (value, type) => {
    if (value === undefined || value === null || value === '') return true; // Empty values are allowed
    
    // Clean the value by removing any whitespace, carriage returns, or line feeds
    const cleanValue = String(value).trim().replace(/[\r\n]+/g, '');
    
    switch (type) {
      case 'number':
        return !isNaN(cleanValue);
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanValue);
      case 'url':
        try {
          new URL(cleanValue);
          return true;
        } catch {
          return false;
        }
      case 'telephone':
        // Telephone format validation - must start with "+" followed by numbers
        // We're being flexible with the format (no specific country code requirements)
        return /^\+[0-9]{6,15}$/.test(cleanValue.replace(/[\s-]/g, ''));
      default:
        return true;
    }
  };

  // Parse CSV content
  const parseCSV = (content) => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      throw new Error('CSV file is empty');
    }

    // Parse header
    const headers = lines[0].split(',').map(header => header.trim().replace(/^"|"$/g, ''));
    
    // Parse rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Simple CSV parsing - split by comma and handle quoted values
      const values = [];
      let currentValue = '';
      let inQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue.trim().replace(/^"|"$/g, ''));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim().replace(/^"|"$/g, ''));
      
      // Create row object
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      rows.push(row);
    }

    return { headers, rows };
  };

  // Handle initial file upload and parsing
  const handleFileUpload = async (event) => {
    try {
      console.log('Starting CSV file upload...');
      setUploading(true);
      setError(null);
      setSuccess(null);
      setProgress({ current: 0, total: 0, message: 'Reading file...', isProcessing: true });

      const file = event.target.files[0];
      if (!file) {
        console.log('No file selected');
        return;
      }
      console.log('File selected:', file.name);

      // Validate file type
      const fileType = file.name.split('.').pop().toLowerCase();
      if (fileType !== 'csv') {
        throw new Error('Invalid file type. Please upload a CSV file (.csv)');
      }

      // Read the CSV file
      const reader = new FileReader();
      
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setError('Error reading file: ' + error.message);
      };

      reader.onload = async (e) => {
        try {
          console.log('File read successfully');
          setProgress(prev => ({ ...prev, message: 'Processing CSV...', isProcessing: true }));
          
          const csvContent = e.target.result;
          const { headers, rows } = parseCSV(csvContent);
          console.log('CSV parsed:', { headers, rowCount: rows.length });

          if (rows.length === 0) {
            throw new Error('No data found in CSV file');
          }

          // Store CSV data and headers for mapping
          setCsvData(rows);
          setCsvHeaders(headers);
          
          // Auto-detect property number column
          const propertyNumberHeaders = ['Location', 'Property Number', 'PropertyNumber', 'property_number', 'Hotel ID', 'ID'];
          const detectedPropertyHeader = headers.find(header => 
            propertyNumberHeaders.some(ph => header.toLowerCase() === ph.toLowerCase())
          );
          
          if (detectedPropertyHeader) {
            setPropertyNumberColumn(detectedPropertyHeader);
          }

          // Initialize column mappings with auto-matching
          const initialMappings = {};
          const autoMatched = new Set();
          
          headers.forEach(header => {
            const matchedField = autoMatchColumn(header);
            initialMappings[header] = matchedField;
            
            // Track which columns were auto-matched (excluding empty matches)
            if (matchedField) {
              autoMatched.add(header);
            }
          });
          
          setColumnMappings(initialMappings);
          setAutoMatchedColumns(autoMatched);

          // Move to mapping step
          setStep('mapping');
          setProgress({ current: 0, total: 0, message: '', isProcessing: false });

          // Reset file input
          event.target.value = '';

        } catch (error) {
          console.error('Error processing CSV:', error);
          setError(error.message);
          setStep('upload');
        } finally {
          setProgress(prev => ({ ...prev, isProcessing: false }));
        }
      };

      reader.readAsText(file, 'UTF-8');

    } catch (error) {
      console.error('Error handling file upload:', error);
      setError(error.message);
    } finally {
      setUploading(false);
      setProgress(prev => ({ ...prev, current: 0, total: 0, message: '', isProcessing: false }));
    }
  };

  // Handle column mapping changes
  const handleColumnMappingChange = (csvHeader, categoryFieldName) => {
    setColumnMappings(prev => ({
      ...prev,
      [csvHeader]: categoryFieldName
    }));
    
    // Remove from auto-matched set since this is now manually set
    setAutoMatchedColumns(prev => {
      const newSet = new Set(prev);
      newSet.delete(csvHeader);
      return newSet;
    });
    
    // Update value filters when mapping changes
    updateValueFilters();
  };

  // Analyze unique values for mapped columns
  const analyzeColumnValues = useCallback(() => {
    if (!csvData || !Object.keys(columnMappings).length) return {};
    
    const analysis = {};
    
    // Only analyze columns that are mapped to categories
    Object.entries(columnMappings).forEach(([csvHeader, categoryField]) => {
      if (categoryField && csvHeader !== propertyNumberColumn) {
        const uniqueValues = new Set();
        
        csvData.forEach(row => {
          const value = String(row[csvHeader] || '').trim();
          if (value) {
            uniqueValues.add(value);
          }
        });
        
        analysis[csvHeader] = {
          categoryField,
          uniqueValues: Array.from(uniqueValues).sort(),
          totalRows: csvData.length,
          nonEmptyRows: csvData.filter(row => String(row[csvHeader] || '').trim()).length
        };
      }
    });
    
    return analysis;
  }, [csvData, columnMappings, propertyNumberColumn]);

  // Update value filters based on current mappings
  const updateValueFilters = useCallback(() => {
    const analysis = analyzeColumnValues();
    const newFilters = {};
    
    Object.entries(analysis).forEach(([csvHeader, data]) => {
      // Initialize all values as selected by default
      newFilters[csvHeader] = {};
      data.uniqueValues.forEach(value => {
        newFilters[csvHeader][value] = true;
      });
    });
    
    setValueFilters(newFilters);
  }, [analyzeColumnValues]);

  // Handle value filter changes
  const handleValueFilterChange = (csvHeader, value, isChecked) => {
    setValueFilters(prev => ({
      ...prev,
      [csvHeader]: {
        ...prev[csvHeader],
        [value]: isChecked
      }
    }));
  };

  // Get available category options for mapping
  const getAvailableCategoryOptions = () => {
    const options = [{ value: '', label: 'Ignore this column' }];
    
    categories.forEach(group => {
      group.items.forEach(item => {
        if (!item.isArchived) {
          options.push({
            value: item.fieldName,
            label: `${item.title} (${item.type})`,
            group: group.title,
            type: item.type
          });
        }
      });
    });
    
    return options;
  };

  // Get category name by field name
  const getCategoryName = (fieldName) => {
    for (const group of categories) {
      for (const item of group.items) {
        if (item.fieldName === fieldName) {
          return item.title;
        }
      }
    }
    return fieldName;
  };

  // Detect duplicate property numbers in CSV data, considering value filters
  const detectDuplicates = useCallback(() => {
    if (!csvData || !propertyNumberColumn) return {};

    const propertyNumberCounts = {};
    const duplicateEntries = {};

    // Get mapped category fields for filtering check
    const mappedHeaders = Object.keys(columnMappings).filter(h => columnMappings[h] && h !== propertyNumberColumn);

    // First pass: count occurrences and collect data (only for rows that would pass value filters)
    csvData.forEach((row, index) => {
      const rawPropertyNumber = String(row[propertyNumberColumn] || '').trim();
      const propertyNumber = extractPropertyNumber(rawPropertyNumber);
      
      if (propertyNumber) {
        // Check if this row would be filtered out based on value filters
        let shouldSkipRow = false;
        for (const header of mappedHeaders) {
          const value = String(row[header] || '').trim();
          if (value && valueFilters[header] && !valueFilters[header][value]) {
            shouldSkipRow = true;
            break;
          }
        }
        
        // Only count rows that would NOT be filtered out
        if (!shouldSkipRow) {
          if (!propertyNumberCounts[propertyNumber]) {
            propertyNumberCounts[propertyNumber] = [];
          }
          propertyNumberCounts[propertyNumber].push({
            rowIndex: index,
            rawValue: rawPropertyNumber,
            data: row
          });
        }
      }
    });

    // Second pass: identify duplicates (only among non-filtered rows)
    Object.entries(propertyNumberCounts).forEach(([propertyNumber, entries]) => {
      if (entries.length > 1) {
        duplicateEntries[propertyNumber] = entries;
      }
    });

    return duplicateEntries;
  }, [csvData, propertyNumberColumn, columnMappings, valueFilters, extractPropertyNumber]);

  // Initialize value filters when mappings change
  useEffect(() => {
    if (csvData && Object.keys(columnMappings).length > 0) {
      updateValueFilters();
    }
  }, [columnMappings, csvData, updateValueFilters]);

  // Recalculate duplicates when value filters change
  useEffect(() => {
    if (csvData && propertyNumberColumn && Object.keys(valueFilters).length > 0) {
      // Clear previously resolved duplicates if the filtered data no longer has duplicates
      const currentDuplicates = detectDuplicates();
      const currentDuplicateKeys = Object.keys(currentDuplicates);
      
      // Remove resolutions for property numbers that are no longer duplicates
      const updatedResolutions = {};
      Object.entries(duplicateResolutions).forEach(([propertyNumber, rowIndex]) => {
        if (currentDuplicateKeys.includes(propertyNumber)) {
          updatedResolutions[propertyNumber] = rowIndex;
        }
      });
      
      setDuplicateResolutions(updatedResolutions);
      
      // If we were on the duplicates step and there are no more duplicates, go back to mapping
      if (step === 'duplicates' && currentDuplicateKeys.length === 0) {
        setStep('mapping');
        setDuplicatesReviewed(true); // Mark as reviewed since filtering resolved the duplicates
      }
    }
  }, [valueFilters, csvData, propertyNumberColumn, detectDuplicates, duplicateResolutions, step]);

  // Handle duplicate resolution
  const handleDuplicateResolution = (propertyNumber, selectedRowIndex) => {
    setDuplicateResolutions(prev => ({
      ...prev,
      [propertyNumber]: selectedRowIndex
    }));
  };

  // Process CSV data with mappings
  const processCSVData = async () => {
    try {
      console.log('Starting CSV processing with mappings...');
      setStep('processing');
      setError(null);
      setSuccess(null);

      if (!propertyNumberColumn) {
        throw new Error('Please select a property number column');
      }

      // Filter out duplicates based on user selections first
      let filteredCsvData = [...csvData];
      
      // If there are duplicate resolutions, filter the data
      if (Object.keys(duplicateResolutions).length > 0) {
        const duplicateEntries = detectDuplicates();
        const rowsToExclude = new Set();
        
        // For each duplicate property number, exclude all rows except the selected one
        Object.entries(duplicateEntries).forEach(([propertyNumber, entries]) => {
          const selectedRowIndex = duplicateResolutions[propertyNumber];
          entries.forEach(entry => {
            if (entry.rowIndex !== selectedRowIndex) {
              rowsToExclude.add(entry.rowIndex);
            }
          });
        });
        
        // Filter out excluded rows
        filteredCsvData = csvData.filter((_, index) => !rowsToExclude.has(index));
        
        console.log(`Filtered out ${rowsToExclude.size} duplicate rows, processing ${filteredCsvData.length} rows`);
      }

      // Now set progress with the correct total
      setProgress({ current: 0, total: filteredCsvData.length, message: 'Starting import...', isProcessing: true });

      // Get mapped category fields (excluding ignored columns)
      const categoryFields = [];
      Object.entries(columnMappings).forEach(([csvHeader, categoryFieldName]) => {
        if (categoryFieldName && csvHeader !== propertyNumberColumn) {
          // Find the category info
          for (const group of categories) {
            for (const item of group.items) {
              if (item.fieldName === categoryFieldName) {
                categoryFields.push({
                  csvHeader,
                  title: item.title,
                  fieldName: item.fieldName,
                  type: item.type
                });
                break;
              }
            }
          }
        }
      });

      if (categoryFields.length === 0) {
        throw new Error('Please map at least one CSV column to a category field');
      }

      console.log('Processing with category fields:', categoryFields);

      // Start a batch write
      const batch = writeBatch(db);
      let updatedCount = 0;
      let skippedCount = 0;
      const errors = [];
      const processedPropertyNumbers = new Set();

              // Process each row
        for (let i = 0; i < filteredCsvData.length; i++) {
          const row = filteredCsvData[i];
          let propertyNumber = String(row[propertyNumberColumn] || '').trim();
          
          // Extract property number if it's in Location format
          if (propertyNumberColumn.toLowerCase() === 'location') {
            propertyNumber = extractPropertyNumber(propertyNumber);
          }
          
          // Update progress
          setProgress(prev => ({ 
            ...prev, 
            current: i + 1, 
            message: `Processing hotel ${propertyNumber} (${i + 1}/${filteredCsvData.length})...` 
          }));

          // Check if row should be filtered out based on value filters
          let shouldSkipRow = false;
          for (const field of categoryFields) {
            const value = String(row[field.csvHeader] || '').trim();
            if (value && valueFilters[field.csvHeader] && !valueFilters[field.csvHeader][value]) {
              console.log(`Filtering out row ${i + 1}: ${field.csvHeader} = "${value}" is not selected`);
              shouldSkipRow = true;
              break;
            }
          }
          
          if (shouldSkipRow) {
            skippedCount++;
            continue;
          }

          // Validate property number
          if (!validatePropertyNumber(propertyNumber)) {
            console.log('Invalid property number:', propertyNumber);
            errors.push(`Skipped row ${i + 1}: Invalid Property Number`);
            skippedCount++;
            continue;
          }

        // Check for duplicates
        if (processedPropertyNumbers.has(propertyNumber)) {
          console.log('Duplicate property number found:', propertyNumber);
          errors.push(`Skipped duplicate Property Number: ${propertyNumber}`);
          skippedCount++;
          continue;
        }
        processedPropertyNumbers.add(propertyNumber);

        try {
          // Get the hotel document - try lowercase first (document IDs are stored in lowercase)
          const propertyNumberLower = propertyNumber.toLowerCase();
          let hotelRef = doc(db, 'hotels', propertyNumberLower);
          let hotelDoc = await getDoc(hotelRef);
          
          if (!hotelDoc.exists()) {
            // If not found by ID, try searching by property number in categoryData
            console.log(`Hotel not found by ID '${propertyNumberLower}', searching by property number...`);
            
            try {
              const hotelQuery = query(
                collection(db, 'hotels'),
                where('categoryData.category_propertynumber.value', '==', propertyNumber.toUpperCase())
              );
              const querySnapshot = await getDocs(hotelQuery);
              
              if (!querySnapshot.empty) {
                const foundDoc = querySnapshot.docs[0];
                hotelRef = foundDoc.ref;
                hotelDoc = foundDoc;
                console.log(`Found hotel by property number search: ${foundDoc.id}`);
              } else {
                // Try original case as well
                const hotelQuery2 = query(
                  collection(db, 'hotels'),
                  where('categoryData.category_propertynumber.value', '==', propertyNumber)
                );
                const querySnapshot2 = await getDocs(hotelQuery2);
                
                if (!querySnapshot2.empty) {
                  const foundDoc = querySnapshot2.docs[0];
                  hotelRef = foundDoc.ref;
                  hotelDoc = foundDoc;
                  console.log(`Found hotel by property number search (original case): ${foundDoc.id}`);
                } else {
                  console.log('Hotel not found by any method:', propertyNumber);
                  errors.push(`Skipped hotel ${propertyNumber}: Not found in database`);
                  skippedCount++;
                  continue;
                }
              }
            } catch (searchError) {
              console.error(`Error searching for hotel ${propertyNumber}:`, searchError);
              errors.push(`Skipped hotel ${propertyNumber}: Error during search - ${searchError.message}`);
              skippedCount++;
              continue;
            }
          }

          // Get current hotel data
          const currentHotel = hotelDoc.data();
          if (!currentHotel) {
            console.log('Invalid hotel data:', propertyNumber);
            errors.push(`Skipped hotel ${propertyNumber}: Invalid data`);
            skippedCount++;
            continue;
          }

          // Prepare category updates and check for changes
          const categoryData = {};
          let hasChanges = false;
          let validationErrors = [];

          categoryFields.forEach(field => {
            const newValue = row[field.csvHeader];
            if (newValue !== undefined && newValue !== null && newValue !== '') {
              console.log(`Processing field ${field.title} for hotel ${propertyNumber}:`, newValue);
              
              // Clean the value before validation and storage
              let cleanValue = String(newValue).trim().replace(/[\r\n]+/g, '');
              
              // Special handling for telephone numbers
              if (field.type === 'telephone') {
                if (cleanValue && !cleanValue.startsWith('+')) {
                  cleanValue = `+${cleanValue}`;
                }
              }
              
              // Validate the cleaned value based on field type
              if (!validateCategoryValue(cleanValue, field.type)) {
                validationErrors.push(`Invalid ${field.type} value for ${field.title}: ${cleanValue}`);
                return;
              }

              const categoryKey = `category_${field.fieldName.toLowerCase()}`;
              const currentValue = currentHotel.categoryData?.[categoryKey]?.value;
              
              // Compare cleaned values
              if (cleanValue !== String(currentValue || '').trim()) {
                hasChanges = true;
                categoryData[categoryKey] = {
                  value: cleanValue,
                  updatedAt: new Date(),
                  updatedBy: user.email
                };
                console.log(`Value changed for ${field.title}:`, { old: currentValue, new: cleanValue });
              }
            }
          });

          // If there are validation errors, skip this hotel
          if (validationErrors.length > 0) {
            console.log('Validation errors for hotel:', propertyNumber, validationErrors);
            errors.push(`Skipped hotel ${propertyNumber} due to validation errors: ${validationErrors.join(', ')}`);
            skippedCount++;
            continue;
          }

          if (hasChanges) {
            // Update hotel document only if there are changes
            console.log(`Updating hotel ${propertyNumber} with changes:`, categoryData);
            batch.update(hotelRef, {
              categoryData: {
                ...currentHotel.categoryData,
                ...categoryData
              },
              lastUpdated: new Date(),
              lastUpdatedBy: user.email
            });
            updatedCount++;
          } else {
            console.log(`No changes needed for hotel ${propertyNumber}`);
            skippedCount++;
          }
        } catch (err) {
          console.error(`Error processing hotel ${propertyNumber}:`, err);
          errors.push(`Error processing hotel ${propertyNumber}: ${err.message}`);
          skippedCount++;
        }
      }

      // Commit the batch only if there are updates
      if (updatedCount > 0) {
        setProgress(prev => ({ ...prev, message: 'Saving changes...' }));
        try {
          await batch.commit();
          console.log('Batch committed successfully');
        } catch (err) {
          console.error('Error committing batch:', err);
          throw new Error(`Failed to save changes: ${err.message}`);
        }
      }

              // Calculate filtering stats
        const totalRows = csvData.length;
        const processedRows = updatedCount + (skippedCount - errors.length);
        const filteredRows = totalRows - processedRows - errors.length;

        // Show success message
        let message = updatedCount > 0 
          ? `Successfully updated ${updatedCount} hotels`
          : 'No changes were needed';
          
        if (filteredRows > 0) {
          message += `\n${filteredRows} rows were filtered out based on your selections`;
        }
        if (skippedCount > 0) {
          message += `\n${skippedCount} hotels were skipped`;
        }
        if (errors.length > 0) {
          message += `\nWarnings/Errors:\n${errors.slice(0, 10).join('\n')}`;
          if (errors.length > 10) {
            message += `\n... and ${errors.length - 10} more`;
          }
        }
        console.log('Final status:', message);
      
      // Clear progress and show success
      setProgress({ current: 0, total: 0, message: '', isProcessing: false });
      setSuccess(message);

    } catch (error) {
      console.error('Error processing CSV data:', error);
      setError(error.message);
      setStep('mapping');
      setProgress({ current: 0, total: 0, message: '', isProcessing: false });
    } finally {
      // Ensure processing state is cleared
      setProgress(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // Reset the component state
  const resetComponent = () => {
    setStep('upload');
    setCsvData(null);
    setCsvHeaders([]);
    setColumnMappings({});
    setAutoMatchedColumns(new Set());
    setValueFilters({});
    setDuplicates({});
    setDuplicateResolutions({});
    setDuplicatesReviewed(false);
    setPropertyNumberColumn('');
    setError(null);
    setSuccess(null);
    setProgress({ current: 0, total: 0, message: '', isProcessing: false });
  };

  // Render component only if user is admin
  if (!user?.role === 'admin') {
    return (
      <Box p={3}>
        <Typography color="error">
          You don't have permission to access this feature.
        </Typography>
      </Box>
    );
  }

  const renderUploadStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload a CSV file to automatically map and import data into your hotel categories. 
        The system will detect common column patterns and help you match them to your category fields.
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3
      }}>
        {/* CSV Data Import Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            flex: 1, 
            p: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TableChartIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Category Data Import
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            CSV with property numbers and category field values to update existing hotels
          </Typography>
        </Paper>
        
        {/* Smart Mapping Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            flex: 1, 
            p: 2, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SyncIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Smart Mapping
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Automatic column detection with manual adjustment and data filtering options
          </Typography>
        </Paper>
      </Box>
      
      <Box sx={{ 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 2,
        borderRadius: 2,
        bgcolor: 'rgba(0, 0, 0, 0.02)',
        border: '2px dashed',
        borderColor: 'divider'
      }}>
        <Button
          variant="contained"
          component="label"
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadFileIcon />}
          disabled={uploading}
          size="large"
          sx={{ 
            px: 4,
            py: 1,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 'bold'
          }}
        >
          {uploading ? 'Processing...' : 'Select CSV File'}
          <input
            type="file"
            hidden
            accept=".csv"
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </Button>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          <Box component="span" sx={{ fontWeight: 'medium', color: 'info.main' }}>
            Supported format:
          </Box>
          {' '}CSV files with headers, property numbers, and category data
        </Typography>
      </Box>

      {(uploading || progress.isProcessing) && (
        <Box sx={{ 
          mt: 3,
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          p: 2,
          bgcolor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            {progress.message || 'Processing CSV file...'}
          </Typography>
        </Box>
      )}
    </>
  );

  const renderMappingStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Map your CSV columns to category fields. Select "Ignore this column" for columns you don't want to import.
      </Typography>

      {/* Property Number Column Selection */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Property Number Column
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Select the column containing property numbers</InputLabel>
          <Select
            value={propertyNumberColumn}
            onChange={(e) => setPropertyNumberColumn(e.target.value)}
            label="Select the column containing property numbers"
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  width: 350,
                  '& .MuiMenu-list': {
                    maxHeight: 280,
                    overflow: 'auto',
                  },
                },
              },
              MenuListProps: {
                style: {
                  maxHeight: 280,
                  overflow: 'auto',
                },
              },
            }}
          >
            {csvHeaders.map(header => (
              <MenuItem key={header} value={header}>
                {header}
                {csvData && csvData[0] && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    (e.g., {String(csvData[0][header] || '').substring(0, 30)}...)
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Column Mappings */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Column Mappings
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>CSV Column</strong></TableCell>
              <TableCell><strong>Sample Data</strong></TableCell>
              <TableCell><strong>Map to Category Field</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {csvHeaders.map(header => (
              <TableRow key={header}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {header}
                  </Typography>
                  {header === propertyNumberColumn && (
                    <Chip label="Property Number" size="small" color="primary" sx={{ mt: 0.5 }} />
                  )}
                  {autoMatchedColumns.has(header) && columnMappings[header] && header !== propertyNumberColumn && (
                    <Chip label="Auto-matched" size="small" color="success" variant="outlined" sx={{ mt: 0.5, ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {csvData && csvData[0] ? String(csvData[0][header] || '').substring(0, 40) : ''}
                    {csvData && csvData[0] && String(csvData[0][header] || '').length > 40 && '...'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {header === propertyNumberColumn ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Used as Property Number
                    </Typography>
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={columnMappings[header] || ''}
                        onChange={(e) => handleColumnMappingChange(header, e.target.value)}
                        displayEmpty
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 400,
                              width: 300,
                              '& .MuiMenu-list': {
                                maxHeight: 380,
                                overflow: 'auto',
                              },
                            },
                          },
                          MenuListProps: {
                            style: {
                              maxHeight: 380,
                              overflow: 'auto',
                            },
                          },
                        }}
                      >
                        {getAvailableCategoryOptions().map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Mapping Summary:</strong> {Object.values(columnMappings).filter(v => v).length} columns mapped, {Object.values(columnMappings).filter(v => !v).length - (propertyNumberColumn ? 1 : 0)} columns ignored
        </Typography>
        {autoMatchedColumns.size > 0 && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            ✓ Auto-matching found {autoMatchedColumns.size} matches automatically
          </Typography>
        )}
        
        {/* Duplicate Detection */}
        {propertyNumberColumn && csvData && (() => {
          const duplicateEntries = detectDuplicates();
          const duplicateCount = Object.keys(duplicateEntries).length;
          
          if (duplicateCount > 0) {
            const resolvedCount = Object.keys(duplicateResolutions).length;
            const allResolved = resolvedCount === duplicateCount;
            
            return (
                              <Alert severity={allResolved && duplicatesReviewed ? "success" : "warning"} sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Duplicate Property Numbers:</strong> Found {duplicateCount} property number{duplicateCount !== 1 ? 's' : ''} with multiple entries.
                    {allResolved && duplicatesReviewed && (
                      <> All duplicates have been resolved.</>
                    )}
                    {!allResolved && resolvedCount > 0 && (
                      <> {resolvedCount} of {duplicateCount} resolved.</>
                    )}
                  </Typography>
                  {!allResolved && !duplicatesReviewed && (
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 1 }}>
                      💡 Tip: You can also resolve duplicates by using the data filtering options below to exclude unwanted values.
                    </Typography>
                  )}
                {(!allResolved || !duplicatesReviewed) && (
                  <Button
                    size="small"
                    variant="outlined"
                    color={allResolved && duplicatesReviewed ? "success" : "warning"}
                    onClick={() => {
                      setDuplicates(duplicateEntries);
                      setStep('duplicates');
                    }}
                    sx={{ mt: 1 }}
                  >
                    {allResolved && duplicatesReviewed ? "Review Resolutions" : `Review Duplicates (${duplicateCount})`}
                  </Button>
                )}
              </Alert>
            );
          }
          return null;
        })()}
      </Box>

      {/* Value Filtering Section */}
      {Object.values(columnMappings).some(v => v) && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Data Filtering (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which values to import for each mapped column. Uncheck values you want to skip.
          </Typography>
          
          {Object.entries(analyzeColumnValues()).map(([csvHeader, data]) => (
            <Accordion key={csvHeader} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {csvHeader}
                  </Typography>
                  <Chip 
                    label={getCategoryName(data.categoryField)} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Box sx={{ ml: 'auto' }}>
                    <Typography variant="caption" color="text.secondary">
                      {valueFilters[csvHeader] ? Object.values(valueFilters[csvHeader]).filter(Boolean).length : data.uniqueValues.length} of {data.uniqueValues.length} values selected
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Button 
                      size="small" 
                      onClick={() => {
                        const newFilters = {};
                        data.uniqueValues.forEach(value => {
                          newFilters[value] = true;
                        });
                        setValueFilters(prev => ({ ...prev, [csvHeader]: newFilters }));
                      }}
                      sx={{ mr: 1 }}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => {
                        const newFilters = {};
                        data.uniqueValues.forEach(value => {
                          newFilters[value] = false;
                        });
                        setValueFilters(prev => ({ ...prev, [csvHeader]: newFilters }));
                      }}
                    >
                      Select None
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
                    {data.uniqueValues.map(value => {
                      const rowCount = csvData.filter(row => String(row[csvHeader] || '').trim() === value).length;
                      return (
                        <FormControlLabel
                          key={value}
                          control={
                            <Checkbox
                              checked={valueFilters[csvHeader]?.[value] || false}
                              onChange={(e) => handleValueFilterChange(csvHeader, value, e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">{value}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({rowCount} row{rowCount !== 1 ? 's' : ''})
                              </Typography>
                            </Box>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </>
  );

  const renderDuplicatesStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Multiple rows were found with the same property number. Please choose which row to use for each duplicate property.
      </Typography>

      {Object.entries(duplicates).map(([propertyNumber, entries]) => (
        <Paper key={propertyNumber} elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
            Property Number: {propertyNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {entries.length} entries. Select which one to use:
          </Typography>
          
          <TableContainer 
            component={Paper} 
            elevation={1} 
            sx={{ 
              maxHeight: 500, 
              width: '100%',
              overflow: 'auto',
              '& .MuiTable-root': {
                minWidth: 'max-content'
              }
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    minWidth: 80, 
                    width: 80,
                    position: 'sticky', 
                    left: 0, 
                    bgcolor: 'background.paper', 
                    zIndex: 3,
                    borderRight: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <strong>Select</strong>
                  </TableCell>
                  <TableCell sx={{ 
                    minWidth: 60, 
                    width: 60,
                    position: 'sticky', 
                    left: 80, 
                    bgcolor: 'background.paper', 
                    zIndex: 3,
                    borderRight: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <strong>Row</strong>
                  </TableCell>
                  {csvHeaders.map(header => (
                    <TableCell key={header} sx={{ 
                      minWidth: 180, 
                      maxWidth: 250,
                      width: 'auto'
                    }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                          {header}
                        </Typography>
                        {header === propertyNumberColumn && (
                          <Chip label="Property Number" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {columnMappings[header] && header !== propertyNumberColumn && (
                          <Chip 
                            label={getCategoryName(columnMappings[header])} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {!columnMappings[header] && header !== propertyNumberColumn && (
                          <Chip 
                            label="Ignored" 
                            size="small" 
                            color="default" 
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem', opacity: 0.6 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow 
                    key={entry.rowIndex}
                    sx={{ 
                      bgcolor: duplicateResolutions[propertyNumber] === entry.rowIndex ? 'success.light' : 'transparent',
                      opacity: duplicateResolutions[propertyNumber] === entry.rowIndex ? 1 : 0.8,
                      '&:hover': {
                        bgcolor: duplicateResolutions[propertyNumber] === entry.rowIndex ? 'success.light' : 'action.hover'
                      }
                    }}
                  >
                    <TableCell sx={{ 
                      position: 'sticky', 
                      left: 0, 
                      bgcolor: 'inherit', 
                      zIndex: 2,
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      width: 80
                    }}>
                      <Checkbox
                        checked={duplicateResolutions[propertyNumber] === entry.rowIndex}
                        onChange={() => handleDuplicateResolution(propertyNumber, entry.rowIndex)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      position: 'sticky', 
                      left: 80, 
                      bgcolor: 'inherit', 
                      zIndex: 2,
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      width: 60
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {entry.rowIndex + 1}
                      </Typography>
                    </TableCell>
                    {csvHeaders.map(header => (
                      <TableCell key={header} sx={{ 
                        minWidth: 180, 
                        maxWidth: 250,
                        width: 'auto'
                      }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: header === propertyNumberColumn ? 'monospace' : 'inherit',
                              fontWeight: header === propertyNumberColumn ? 'bold' : 'normal',
                              color: header === propertyNumberColumn ? 'primary.main' : 'text.primary',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              display: 'block',
                              width: '100%'
                            }}
                          >
                            {String(entry.data[header] || '').substring(0, 100)}
                            {String(entry.data[header] || '').length > 100 && (
                              <span style={{ color: '#666', fontStyle: 'italic' }}>
                                ... ({String(entry.data[header] || '').length} chars)
                              </span>
                            )}
                          </Typography>
                          {String(entry.data[header] || '').trim() === '' && (
                            <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                              (empty)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Resolution Status:</strong> {Object.keys(duplicateResolutions).length} of {Object.keys(duplicates).length} duplicates resolved
        </Typography>
      </Box>
    </>
  );

  const renderProcessingStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {progress.isProcessing ? 'Processing your CSV import...' : 'Import completed!'}
      </Typography>

      {progress.isProcessing && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: theme => alpha(theme.palette.background.paper, 0.6)
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Import Progress
          </Typography>
          
          {progress.total > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {progress.message}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {Math.round((progress.current / progress.total) * 100)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(progress.current / progress.total) * 100}
                sx={{ 
                  height: 8,
                  borderRadius: 4,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4
                  }
                }}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {progress.message || 'Processing...'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {!progress.isProcessing && (success || error) && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          You can now close this dialog or start a new import.
        </Typography>
      )}
    </>
  );

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        resetComponent();
        onClose();
      }}
      maxWidth={step === 'upload' ? 'md' : 'lg'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: step === 'upload' ? 2 : 3,
          maxHeight: '90vh',
          overflow: step === 'upload' ? 'hidden' : 'auto'
        }
      }}
    >
      {step === 'upload' ? (
        // Beautiful split-panel design for upload step
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          height: { md: '490px' }
        }}>
          {/* Left section with visual background and title */}
          <Box sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            p: 3,
            width: { xs: '100%', md: '240px' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <Box>
              <Typography variant="h5" fontWeight="medium" mb={2}>
                Import Category Data
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Upload your CSV file to quickly update hotel category information across your properties.
              </Typography>
            </Box>
            
            <Box sx={{ opacity: 0.8, mt: 4 }}>
              <UploadFileIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
              <Typography variant="body2" fontWeight="medium">
                3-Step Process
              </Typography>
              <Typography variant="body2">
                Upload → Map Columns → Import Data
              </Typography>
            </Box>
          </Box>
          
          {/* Right section with content */}
          <Box sx={{ 
            flex: 1, 
            p: 3,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <DialogTitle sx={{ px: 0, pt: 0 }}>
              <Typography variant="h6" fontWeight="bold" component="div">
                Select CSV File to Import
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, overflow: 'auto' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
                    {success}
                  </Typography>
                </Alert>
              )}

              {renderUploadStep()}
            </DialogContent>
            
            <DialogActions sx={{ p: 0, pt: 2, justifyContent: 'flex-end' }}>
              <Button 
                onClick={() => {
                  resetComponent();
                  onClose();
                }}
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  minWidth: '100px'
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Box>
        </Box>
      ) : (
        // Traditional design for mapping and processing steps
        <>
          <DialogTitle sx={{ 
            m: 0, 
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Import from CSV</Typography>
              {step === 'mapping' && (
                <Chip label="Step 2: Map Columns" size="small" color="primary" />
              )}
              {step === 'duplicates' && (
                <Chip label="Step 3: Resolve Duplicates" size="small" color="warning" />
              )}
              {step === 'processing' && (
                <Chip label="Step 4: Processing" size="small" color="primary" />
              )}
            </Box>
            <IconButton
              onClick={() => {
                resetComponent();
                onClose();
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3, minHeight: 400 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
                  {success}
                </Typography>
              </Alert>
            )}

            {step === 'mapping' && renderMappingStep()}
            {step === 'duplicates' && renderDuplicatesStep()}
            {step === 'processing' && renderProcessingStep()}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            {step === 'mapping' && (
              <>
                <Button 
                  onClick={() => setStep('upload')}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Back
                </Button>
                <Button 
                  onClick={() => {
                    const duplicateEntries = detectDuplicates();
                    const duplicateCount = Object.keys(duplicateEntries).length;
                    const resolvedCount = Object.keys(duplicateResolutions).length;
                    
                    if (duplicateCount > 0 && (resolvedCount < duplicateCount || !duplicatesReviewed)) {
                      // Go to duplicates step if there are unresolved duplicates or they haven't been reviewed
                      setDuplicates(duplicateEntries);
                      setStep('duplicates');
                    } else {
                      // Either no duplicates or all duplicates have been resolved, proceed to import
                      processCSVData();
                    }
                  }}
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={!propertyNumberColumn || Object.values(columnMappings).filter(v => v).length === 0}
                  sx={{ borderRadius: 2 }}
                >
                  Continue
                </Button>
              </>
            )}
            
            {step === 'duplicates' && (
              <>
                <Button 
                  onClick={() => setStep('mapping')}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Back
                </Button>
                <Button 
                  onClick={() => {
                    setDuplicatesReviewed(true);
                    processCSVData();
                  }}
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={Object.keys(duplicateResolutions).length !== Object.keys(duplicates).length}
                  sx={{ borderRadius: 2 }}
                >
                  Import Data
                </Button>
              </>
            )}
            
            {step === 'processing' && (
              <>
                {!progress.isProcessing && (
                  <Button 
                    onClick={() => setStep('upload')}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  >
                    Import Another File
                  </Button>
                )}
                <Button 
                  onClick={() => {
                    resetComponent();
                    onClose();
                  }}
                  variant={progress.isProcessing ? "outlined" : "contained"}
                  sx={{ borderRadius: 2 }}
                  disabled={progress.isProcessing}
                >
                  {progress.isProcessing ? 'Processing...' : 'Close'}
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CategoryCSVImport; 